import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import { trackSaleDeleted } from '../services/analytics';
import '../styles/v2.css';

/* ── helpers ── */
function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
function formatProductName(s) {
  let name = s.product_name || '';
  if (s.variety) name += ` - ${s.variety}`;
  if (s.brand)   name += ` (${s.brand})`;
  if (s.size)    name += ` [${s.size}]`;
  return name;
}
function getInitials(name) {
  if (!name) return '??';
  return name.split(/[\s\-()\[\]]+/).filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2);
}
const COLORS = [
  'linear-gradient(135deg,#f87171,#ef4444)',
  'linear-gradient(135deg,#fbbf24,#d97706)',
  'linear-gradient(135deg,#60a5fa,#3b82f6)',
  'linear-gradient(135deg,#a78bfa,#8b5cf6)',
  'linear-gradient(135deg,#34d399,#10b981)',
  'linear-gradient(135deg,#f472b6,#ec4899)',
  'linear-gradient(135deg,#fb923c,#ea580c)',
];

/* ── Toast ── */
function Toast({ message, severity, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 5000); return () => clearTimeout(t); }, [onClose]);
  const c = severity === 'success'
    ? { bg: '#f0fdf4', border: '#a7f3d0', text: '#065f46', icon: '#10b981' }
    : { bg: '#fef2f2', border: '#fecaca', text: '#b91c1c', icon: '#ef4444' };
  return (
    <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, maxWidth: 400, width: 'calc(100% - 48px)', background: c.bg, border: `1px solid ${c.border}`, borderRadius: 14, padding: '14px 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 20, height: 20, borderRadius: '50%', background: c.icon, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {severity === 'success'
          ? <svg style={{ width: 12, height: 12, color: 'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
          : <svg style={{ width: 12, height: 12, color: 'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>}
      </div>
      <p style={{ flex: 1, fontSize: 13, fontWeight: 500, color: c.text, margin: 0 }}>{message}</p>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.text, opacity: 0.5, padding: 0 }}>
        <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
      </button>
    </div>
  );
}

/* ── Modal ── */
function Modal({ open, onClose, title, children, footer }) {
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'relative', background: 'white', borderRadius: 20, width: '100%', maxWidth: 440, boxShadow: '0 24px 60px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #f1f5f1' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, borderRadius: 8, display: 'flex' }}>
            <svg style={{ width: 18, height: 18 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div style={{ padding: '20px 24px' }}>{children}</div>
        {footer && <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f1', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>{footer}</div>}
      </div>
    </div>
  );
}

/* ── Detail row ── */
function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f8fafc' }}>
      <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13, color: '#1e293b', fontWeight: 600, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>{value || '—'}</span>
    </div>
  );
}

const PAGE_SIZES = [6, 12, 18];

export default function ViewSalesV2() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(params.get('from') || '');
  const [dateTo, setDateTo]     = useState(params.get('to')   || '');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(6);
  const [selectedSale, setSelectedSale] = useState(null);
  const [saleToDelete, setSaleToDelete] = useState(null);
  const [toast, setToast] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 900);
  const searchRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    axiosInstance.get('/sales')
      .then(res => setSales(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { setPage(0); }, [search, dateFrom, dateTo]);

  const filtered = sales.filter(s => {
    const q = search.toLowerCase().trim();
    const matchSearch = !q || `${s.product_name} ${s.brand} ${s.variety} ${s.size} ${s.customer_name || ''}`.toLowerCase().includes(q);
    const saleDay = s.sale_date ? s.sale_date.split('T')[0] : '';
    const matchFrom = !dateFrom || saleDay >= dateFrom;
    const matchTo   = !dateTo   || saleDay <= dateTo;
    return matchSearch && matchFrom && matchTo;
  });
  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice(page * pageSize, page * pageSize + pageSize);

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/sales/${saleToDelete.sale_id}`);
      trackSaleDeleted(saleToDelete.sale_id);
      setSales(prev => prev.filter(s => s.sale_id !== saleToDelete.sale_id));
      setToast({ message: 'Sale deleted successfully.', severity: 'success' });
    } catch {
      setToast({ message: 'Failed to delete sale.', severity: 'error' });
    } finally {
      setSaleToDelete(null);
    }
  };

  const statusColor = (idx) => COLORS[idx % COLORS.length];

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid #e2e8f0', borderTopColor: '#10b981', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>Loading sales…</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Page header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ color: '#0f172a', fontSize: 20, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>Sales</h1>
          <p style={{ color: '#94a3b8', fontSize: 13, margin: '2px 0 0' }}>{filtered.length} record{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => navigate('/new/dashboard/sales/add')}
          className="v2-btn-submit"
          style={{ width: 'auto', padding: '10px 20px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <svg style={{ width: 15, height: 15 }} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Add Sale
        </button>
      </div>

      {/* ── Search ── */}
      <div className="v2-card" style={{ padding: '14px 18px' }}>
        <div style={{ position: 'relative' }}>
          <svg style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            ref={searchRef}
            className="v2-input"
            style={{ paddingLeft: 38 }}
            type="text"
            placeholder="Search by product, brand, variety, size or customer…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, display: 'flex' }}>
              <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          )}
        </div>
      </div>

      {/* ── Active date filter chip ── */}
      {(dateFrom || dateTo) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderRadius: 12, background: '#f0fdf4', border: '1.5px solid #a7f3d0' }}>
          <svg style={{ width: 15, height: 15, color: '#10b981', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 9v7.5" /></svg>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#065f46', flex: 1 }}>
            Filtered: {dateFrom} — {dateTo}
          </span>
          <button onClick={() => { setDateFrom(''); setDateTo(''); }}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 8, border: 'none', background: '#10b981', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            <svg style={{ width: 12, height: 12 }} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            Clear
          </button>
        </div>
      )}

      {/* ── Content ── */}
      {paginated.length === 0 ? (
        <div className="v2-card" style={{ padding: 48, textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <svg style={{ width: 24, height: 24, color: '#94a3b8' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
            </svg>
          </div>
          <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>{search ? 'No sales match your search.' : (dateFrom || dateTo) ? 'No sales found for this date range.' : 'No sales recorded yet.'}</p>
        </div>

      ) : isDesktop ? (
        /* ── Desktop: table ── */
        <div className="v2-card" style={{ overflow: 'hidden' }}>
          <table className="v2-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f1f5f1' }}>
                {['Product', 'Unit', 'Price (K)', 'Qty', 'Discount (K)', 'Date', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 20px', textAlign: h === 'Product' ? 'left' : 'center', fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.map((sale, i) => (
                <tr key={sale.sale_id} style={{ borderBottom: i < paginated.length - 1 ? '1px solid #f8faf8' : 'none' }}>
                  <td style={{ padding: '13px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 9, background: statusColor(i), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ color: 'white', fontSize: 11, fontWeight: 900 }}>{getInitials(sale.product_name)}</span>
                      </div>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#1e293b', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {formatProductName(sale)}
                      </p>
                    </div>
                  </td>
                  <td style={{ padding: '13px 20px', textAlign: 'center', fontSize: 13, color: '#475569' }}>{sale.unit_type || '—'}</td>
                  <td style={{ padding: '13px 20px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{sale.retail_price}</td>
                  <td style={{ padding: '13px 20px', textAlign: 'center', fontSize: 13, color: '#475569' }}>{sale.quantity}</td>
                  <td style={{ padding: '13px 20px', textAlign: 'center', fontSize: 13, color: sale.discount > 0 ? '#ef4444' : '#94a3b8' }}>
                    {sale.discount > 0 ? `−${sale.discount}` : '—'}
                  </td>
                  <td style={{ padding: '13px 20px', textAlign: 'center', fontSize: 13, color: '#64748b', whiteSpace: 'nowrap' }}>{formatDate(sale.sale_date)}</td>
                  <td style={{ padding: '13px 20px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                      <button onClick={() => navigate(`/new/dashboard/sales/edit/${sale.sale_id}`)} title="Edit" style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#10b981'; e.currentTarget.style.color = '#10b981'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; }}>
                        <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" /></svg>
                      </button>
                      <button onClick={() => setSelectedSale(sale)} title="Details" style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#60a5fa'; e.currentTarget.style.color = '#3b82f6'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; }}>
                        <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>
                      </button>
                      <button onClick={() => setSaleToDelete(sale)} title="Delete" style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#fecaca'; e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#fef2f2'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = 'white'; }}>
                        <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      ) : (
        /* ── Mobile: cards ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {paginated.map((sale, i) => (
            <div key={sale.sale_id} className="v2-card" style={{ padding: 18, overflow: 'hidden' }}>
              {/* Card header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: statusColor(i), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: 'white', fontSize: 12, fontWeight: 900 }}>{getInitials(sale.product_name)}</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formatProductName(sale)}
                  </p>
                  <p style={{ margin: '3px 0 0', fontSize: 12, color: '#94a3b8' }}>{formatDate(sale.sale_date)}</p>
                </div>
              </div>

              {/* Stats grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, paddingTop: 14, borderTop: '1px solid #f1f5f1', marginBottom: 14 }}>
                {[
                  { label: 'Price (K)', value: sale.retail_price },
                  { label: 'Qty', value: sale.quantity },
                  { label: 'Unit', value: sale.unit_type || '—' },
                  { label: 'Discount', value: sale.discount > 0 ? `K ${sale.discount}` : '—' },
                ].map(item => (
                  <div key={item.label} style={{ textAlign: 'center' }}>
                    <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{item.label}</p>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#334155' }}>{item.value}</p>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => navigate(`/new/dashboard/sales/edit/${sale.sale_id}`)} style={{ flex: 1, padding: '8px 0', borderRadius: 10, border: '1.5px solid #e2e8f0', background: 'white', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <svg style={{ width: 13, height: 13 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" /></svg>
                  Edit
                </button>
                <button onClick={() => setSelectedSale(sale)} style={{ flex: 1, padding: '8px 0', borderRadius: 10, border: '1.5px solid #e2e8f0', background: 'white', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <svg style={{ width: 13, height: 13 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>
                  Details
                </button>
                <button onClick={() => setSaleToDelete(sale)} style={{ flex: 1, padding: '8px 0', borderRadius: 10, border: '1.5px solid #fecaca', background: '#fef2f2', color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <svg style={{ width: 13, height: 13 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="v2-card" style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Rows per page:</span>
            <select
              className="v2-input"
              style={{ width: 'auto', padding: '6px 28px 6px 10px', fontSize: 13 }}
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
            >
              {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>
              {page * pageSize + 1}–{Math.min((page + 1) * pageSize, filtered.length)} of {filtered.length}
            </span>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: page === 0 ? '#f8fafc' : 'white', cursor: page === 0 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: page === 0 ? '#cbd5e1' : '#475569' }}>
              <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
            </button>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: page >= totalPages - 1 ? '#f8fafc' : 'white', cursor: page >= totalPages - 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: page >= totalPages - 1 ? '#cbd5e1' : '#475569' }}>
              <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
            </button>
          </div>
        </div>
      )}

      {/* ── Detail modal ── */}
      <Modal open={!!selectedSale} onClose={() => setSelectedSale(null)} title="Sale Details"
        footer={<button onClick={() => setSelectedSale(null)} style={{ padding: '9px 20px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: 'white', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Close</button>}>
        {selectedSale && (
          <div>
            <DetailRow label="Product" value={formatProductName(selectedSale)} />
            <DetailRow label="Retail Price" value={`K ${selectedSale.retail_price}`} />
            <DetailRow label="Quantity" value={selectedSale.quantity} />
            <DetailRow label="Unit" value={selectedSale.unit_type} />
            <DetailRow label="Discount" value={selectedSale.discount > 0 ? `K ${selectedSale.discount}` : 'None'} />
            <DetailRow label="Sale Date" value={formatDate(selectedSale.sale_date)} />
            {selectedSale.customer_name && <DetailRow label="Customer" value={selectedSale.customer_name} />}
          </div>
        )}
      </Modal>

      {/* ── Delete confirm modal ── */}
      <Modal open={!!saleToDelete} onClose={() => setSaleToDelete(null)} title="Delete Sale"
        footer={
          <>
            <button onClick={() => setSaleToDelete(null)} style={{ padding: '9px 20px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: 'white', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleDelete} style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#ef4444,#dc2626)', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Delete</button>
          </>
        }>
        {saleToDelete && (
          <div>
            <p style={{ margin: '0 0 12px', fontSize: 14, color: '#475569', lineHeight: 1.6 }}>
              Are you sure you want to delete this sale? This action cannot be undone.
            </p>
            <div style={{ padding: '12px 16px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca' }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#b91c1c' }}>{formatProductName(saleToDelete)}</p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#94a3b8' }}>{formatDate(saleToDelete.sale_date)} · K {saleToDelete.retail_price} × {saleToDelete.quantity}</p>
            </div>
          </div>
        )}
      </Modal>

      {toast && <Toast message={toast.message} severity={toast.severity} onClose={() => setToast(null)} />}
    </div>
  );
}
